import { useEffect, useRef, useState } from 'react';

export function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = () => setReduced(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return reduced;
}

/**
 * Section entrance reveal. Runs once when the element crosses the trigger
 * line. Mirrors the landing motion spec: threshold 0.15, root margin
 * pulling the trigger 40px up from the viewport bottom.
 */
export function useLandingReveal(options = {}) {
  const { threshold = 0.15, rootMargin = '0px 0px -40px 0px', onReveal } = options;
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const onRevealRef = useRef(onReveal);
  onRevealRef.current = onReveal;

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (onRevealRef.current) onRevealRef.current();
          observer.unobserve(el);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  return [ref, isVisible];
}

/**
 * Fires once when the element reaches the given visibility share
 * (used for view-through analytics such as 50% section visibility).
 */
export function useViewedOnce(threshold, onViewed) {
  const ref = useRef(null);
  const firedRef = useRef(false);
  const onViewedRef = useRef(onViewed);
  onViewedRef.current = onViewed;

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !firedRef.current) {
          firedRef.current = true;
          if (onViewedRef.current) onViewedRef.current();
          observer.unobserve(el);
        }
      },
      { threshold }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return ref;
}

const COUNT_UP_DURATION_MS = 900;

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

/**
 * Counts from 0 to `target` once `active` becomes true. Reduced motion
 * renders the final value immediately.
 */
export function useCountUp(target, active, reducedMotion) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!active) return undefined;
    if (reducedMotion || typeof target !== 'number') {
      setValue(target);
      return undefined;
    }

    let frameId = null;
    const start = performance.now();

    const tick = (now) => {
      const progress = Math.min(1, (now - start) / COUNT_UP_DURATION_MS);
      setValue(Math.round(easeOutCubic(progress) * target));
      if (progress < 1) {
        frameId = requestAnimationFrame(tick);
      }
    };

    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [active, reducedMotion, target]);

  return value;
}
